import ApiService from '../../../services/apiService';
import { userItemsPerListMixin } from '../../../utils/vuex';
import moment from 'moment';
import { mapState } from 'vuex';
import get from 'lodash/get';
const defaultItemsPerPage = 10;
const defaultTableOptions = {
  sortBy: ['id'],
  sortDesc: [false],
  page: 1,
  itemsPerPage: defaultItemsPerPage
};
export default {
  name: 'CoreConnectorsList',
  mixins: [userItemsPerListMixin],
  props: {
    accounts: {
      type: Array,
      required: false,
      default: () => []
    },
    tenants: {
      type: Array,
      required: false,
      default: () => []
    }
  },
  data: function () {
    return {
      cmOptions: {
        tabSize: 4,
        mode: {
          name: 'javascript',
          json: true
        },
        firstLineNumber: 0,
        theme: this.$store.state.app.UiTheme ? 'base16-dark' : 'duotone-light',
        lineNumbers: false,
        line: true,
        foldGutter: true,
        styleActiveLine: true,
        styleSelectedText: true,
        hintOptions: {
          completeSingle: false
        },
        lineWrapping: false
      },
      tableOptions: defaultTableOptions,
      isLoadingConnectors: false,
      searchItem: '',
      connectorDialog: false,
      editConnectorIndex: -1,
      deleteDialog: false,
      deleteBridgeId: null,
      newBridgeType: null,
      bridgeTypes: [],
      bridges: [],
      bridgeValidations: {}
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'UiTheme']),
    tableLocalizations() {
      return {
        headers: [{
          text: this.$i18n_t('Connectors.Connectors.tableHeaders.id'),
          value: 'id',
          align: 'right'
        }, {
          text: this.$i18n_t('Connectors.Connectors.tableHeaders.state'),
          value: 'state'
        }, {
          text: this.$i18n_t('Connectors.Connectors.tableHeaders.name'),
          value: 'name'
        }, {
          text: this.$i18n_t('Connectors.Connectors.tableHeaders.type'),
          value: 'type'
        }, {
          text: this.$i18n_t('Connectors.Connectors.tableHeaders.kind'),
          value: 'table.kind',
          align: 'center'
        }, {
          text: this.$i18n_t('Connectors.Connectors.tableHeaders.account'),
          value: 'tenantId'
        }, {
          text: this.$i18n_t('Connectors.Connectors.tableHeaders.createdAt'),
          value: 'createdAt'
        }, {
          text: '',
          value: '',
          align: 'end',
          sortable: false,
          width: '78'
        }],
        footer: {
          itemsPerPageText: this.$i18n_t('Common.rowsPerPage'),
          itemsPerPageOptions: [10, 20, 50]
        }
      };
    },
    runtimeInstance() {
      return this;
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    hasNoConnectors() {
      return this.bridges.length === 0;
    },
    tableBridges() {
      return this.bridges.map(bridge => {
        const config = this.bridgeConfig(bridge);
        const inbound = get(config, 'manifest.functionScope.invokeDirection.inbound');
        const outbound = get(config, 'manifest.functionScope.invokeDirection.outbound');
        return {
          ...bridge,
          table: {
            kind: inbound && outbound ? 'both' : inbound ? 'inbound' : 'outbound'
          }
        };
      });
    }
  },
  watch: {
    tenants() {
      this.loadData();
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    get,
    showCreateDialog() {
      this.$emit('create');
    },
    handleDeleteConnector(id) {
      this.deleteDialog = false;
      this.deleteBridge(id);
    },
    loadData() {
      this.isLoadingConnectors = true;
      this.bridgeTypes = [];
      this.bridges = [];
      return Promise.all([this.loadBridgeTypes(), this.loadBridgeConfigs()]).then(([types, configs]) => {
        this.bridgeTypes = types.map(type => {
          return {
            ...type,
            name: get(type, 'manifest.displayName')
          };
        }).sort((b1, b2) => {
          if (b1.name > b2.name) {
            return 1;
          }
          if (b1.name < b2.name) {
            return -1;
          }
          return 0;
        });
        this.bridges = configs.map(config => {
          const bridgeType = this.bridgeTypes.find(t => t.type === config.type);
          config.displayName = bridgeType ? get(bridgeType, 'manifest.displayName') : '';
          return config;
        });
      }).finally(() => {
        this.isLoadingConnectors = false;
      });
    },
    bridgeConfigIsInbound(bridge) {
      return get(bridge, 'manifest.functionScope.invokeDirection.inbound', false);
    },
    bridgeConfigIsOutbound(bridge) {
      return get(bridge, 'manifest.functionScope.invokeDirection.outbound', false);
    },
    bridgeConfig(bridge) {
      const foundMatch = this.bridgeTypes.find(config => {
        return config.type === bridge.type;
      });
      return foundMatch;
    },
    bridgeConfigKeys(instance) {
      const bridgeManifest = this.bridgeConfig(instance).manifest;
      let keys = Object.keys(bridgeManifest.config[0]).filter(key => {
        if (get(bridgeManifest, `config[0][${key}].hidden`)) {
          return false;
        }
        // if field is readonly, only show if it has a value
        if (get(bridgeManifest, `config[0][${key}].readonly`)) {
          return !!instance.bridge.config[key];
        }
        const visibleConfig = get(bridgeManifest, `config[0][${key}].visible`) || {};
        return Object.keys(visibleConfig).every(dependentKey => {
          return instance.bridge.config[dependentKey] === visibleConfig[dependentKey];
        });
      });
      return keys.sort((a, b) => {
        const orderIdxA = get(bridgeManifest, `config[0][${a}].orderIdx`) || 0;
        const orderIdxB = get(bridgeManifest, `config[0][${b}].orderIdx`) || 0;
        if (orderIdxA < orderIdxB) {
          return -1;
        }
        if (orderIdxA > orderIdxB) {
          return 1;
        }
        return a.localeCompare(b);
      });
    },
    loadBridgeTypes() {
      return ApiService.getBridgeTypes().then(bridgeTypes => {
        return bridgeTypes;
      });
    },
    loadBridgeConfigs() {
      if (!this.tenants) {
        this.tenants = [];
      }
      const ids = this.tenants.map(acc => acc.id);
      return ApiService.getBridgesForTenants(ids);
    },
    deleteBridge(id) {
      const bridge = this.bridges.find(b => b.id === id);
      let deletePromise = Promise.resolve();
      if (bridge.id) {
        deletePromise = ApiService.deleteBridge(bridge.id);
      }
      deletePromise.then(() => {
        this.loadData();
      }).catch(error => {
        console.error(error);
      });
    },
    localizeDate(date) {
      if (date) {
        moment.locale(this.$store.state.app.language || window.navigator.userLanguage || window.navigator.language);
        return moment(date).format('L LTS');
      }
      return '-';
    }
  }
};